import { useCallback } from 'react';
import { purchasesService } from '../services';
import {
  useResourceCollection,
  useResourceById,
  useCreateResource,
  useUpdateResource,
  useDeleteResource,
  ResourceState,
  SingleResourceState,
  MutationState,
} from './useResource';
import { ResourceQueryOptions } from '../services/resources.service';
import { Purchase } from '../types';

export function usePurchases(
  options?: ResourceQueryOptions
): ResourceState<Purchase> & { meta?: any } {
  const fetchFn = useCallback(
    (opts?: ResourceQueryOptions) =>
      purchasesService.getAll(opts) as unknown as Promise<{ data: Purchase[]; meta?: any }>,
    []
  );
  return useResourceCollection<Purchase>(fetchFn, options);
}

export function usePurchase(
  id: string | null,
  options?: ResourceQueryOptions
): SingleResourceState<Purchase> {
  const numericId = id ? parseInt(id, 10) : null;
  const fetchFn = useCallback(
    (purchaseId: number, opts?: ResourceQueryOptions) =>
      purchasesService.getById(purchaseId, opts) as unknown as Promise<Purchase>,
    []
  );
  return useResourceById<Purchase>(fetchFn, numericId, options);
}

export function useCreatePurchase(
  onSuccess?: (data: Purchase) => void
): MutationState<Purchase> {
  const createFn = useCallback(
    (data: any) => purchasesService.create(data) as unknown as Promise<Purchase>,
    []
  );
  return useCreateResource<Purchase>(createFn, onSuccess);
}

export function useUpdatePurchase(
  onSuccess?: (data: Purchase) => void
): MutationState<Purchase> {
  const updateFn = useCallback(
    (id: string | number, data: any) => {
      const numericId = typeof id === 'string' ? parseInt(id, 10) : id;
      return purchasesService.update(numericId, data) as unknown as Promise<Purchase>;
    },
    []
  );
  return useUpdateResource<Purchase>(updateFn, onSuccess);
}

/**
 * Delete purchase using documentId
 * @param data - Full purchase object containing documentId
 */
export function useDeletePurchase(onSuccess?: () => void): MutationState<void> {
  const deleteFn = useCallback(
    (data: any) => {
      const documentId = data.documentId || data.id?.toString() || data.id;
      return purchasesService.delete(documentId) as unknown as Promise<void>;
    },
    []
  );
  return useDeleteResource(deleteFn, onSuccess);
}
// Auto-generated hook file
